/*
 * AppShell — the role-aware top chrome and segmented plane nav.
 *
 * Exports the pieces App.tsx composes:
 *   - TopBar          (wordmark, connection indicator, role selector)
 *   - PlaneNav        (the six-plane segmented nav)
 *   - ConnIndicator   (green ONLY when /healthz returned 200)
 *   - RoleSelect      (operator | auditor | exec)
 *   - useHashRoute    (client-side view switcher, hash router)
 *   - useHealthPoll   (polls /healthz on an interval for the dot)
 *   - PLANES          (the nav model: id, label, plane ref, accent)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getHealth, MESH_BASE } from "../api/client";
import type { HealthState, PlaneId, Role } from "../api/types";

/* ---------- Nav model ---------- */

export interface PlaneNavItem {
  id: PlaneId;
  label: string;
  /** Spec plane label shown small, e.g. "Transport · IV". */
  plane: string;
  accent: string;
}

export const PLANES: PlaneNavItem[] = [
  { id: "overview", label: "Overview", plane: "Mesh · I–II", accent: "var(--fg)" },
  { id: "cockpit", label: "Cockpit", plane: "Operator · §8", accent: "var(--teal)" },
  { id: "donegate", label: "Done-gate", plane: "Operator · §9", accent: "var(--teal)" },
  { id: "transport", label: "Transport", plane: "Plane IV", accent: "var(--ember)" },
  { id: "trust", label: "Trust", plane: "Plane III", accent: "var(--teal)" },
  { id: "catalog", label: "Catalog", plane: "Plane V", accent: "var(--indigo)" },
  { id: "observability", label: "Observability", plane: "Plane VI", accent: "var(--gold)" },
  { id: "consent", label: "Consent", plane: "Plane VII", accent: "var(--indigo)" },
];

const PLANE_IDS = new Set(PLANES.map((p) => p.id));

/* ---------- Hash router ---------- */

function readHash(): PlaneId {
  const raw = window.location.hash.replace(/^#\/?/, "").split("/")[0];
  return (PLANE_IDS.has(raw as PlaneId) ? raw : "overview") as PlaneId;
}

/** Client-side view switcher. Returns [active plane, navigate]. */
export function useHashRoute(): [PlaneId, (id: PlaneId) => void] {
  const [active, setActive] = useState<PlaneId>(readHash);

  useEffect(() => {
    const onHash = () => setActive(readHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const navigate = useCallback((id: PlaneId) => {
    window.location.hash = `/${id}`;
  }, []);

  return [active, navigate];
}

/** Read the full hash tail after the plane id, e.g. #/transport/<request_id>. */
export function useHashTail(): string {
  const [tail, setTail] = useState(() =>
    window.location.hash.replace(/^#\/?/, "").split("/").slice(1).join("/"),
  );
  useEffect(() => {
    const onHash = () =>
      setTail(window.location.hash.replace(/^#\/?/, "").split("/").slice(1).join("/"));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return tail;
}

/* ---------- Health poll for the connection dot ---------- */

export function useHealthPoll(intervalMs = 5000): HealthState {
  const [state, setState] = useState<HealthState>({
    healthy: false,
    status: 0,
    checkedAt: 0,
  });
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    const tick = async () => {
      const next = await getHealth();
      if (aliveRef.current) setState(next);
    };
    void tick();
    const id = setInterval(() => void tick(), intervalMs);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [intervalMs]);

  return state;
}

/* ---------- Connection indicator ---------- */

export function ConnIndicator({ health }: { health: HealthState }) {
  const never = health.checkedAt === 0;
  const cls = never ? "conn--probing" : health.healthy ? "conn--up" : "conn--down";
  const word = never ? "probing" : health.healthy ? "connected" : "offline";

  return (
    <span
      className={`conn ${cls}`}
      role="status"
      aria-label={`Sidecar ${word}${health.latencyMs ? `, ${health.latencyMs}ms` : ""}`}
      title={
        health.error
          ? `${MESH_BASE}: ${health.error}`
          : `${MESH_BASE} — HTTP ${health.status || "—"}`
      }
    >
      <span className="conn__dot" aria-hidden="true" />
      <span>{word}</span>
      <span className="conn__base">{MESH_BASE}</span>
      {health.healthy && health.latencyMs !== undefined && (
        <span className="conn__base">· {health.latencyMs}ms</span>
      )}
    </span>
  );
}

/* ---------- Role selector ---------- */

export const ROLES: { id: Role; label: string }[] = [
  { id: "operator", label: "Operator" },
  { id: "auditor", label: "Auditor" },
  { id: "exec", label: "Read-only exec" },
];

export function RoleSelect({
  role,
  onChange,
}: {
  role: Role;
  onChange: (r: Role) => void;
}) {
  return (
    <span className="role-select">
      <label htmlFor="role-select">Role</label>
      <select
        id="role-select"
        value={role}
        onChange={(e) => onChange(e.target.value as Role)}
      >
        {ROLES.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label}
          </option>
        ))}
      </select>
    </span>
  );
}

/* ---------- Top bar ---------- */

export function TopBar({
  health,
  role,
  onRoleChange,
}: {
  health: HealthState;
  role: Role;
  onRoleChange: (r: Role) => void;
}) {
  return (
    <header className="topbar">
      <div className="topbar__brand">
        <span className="topbar__wordmark">OpenGov Mesh</span>
        <span className="topbar__tagline">Console · Trust, made provable</span>
      </div>
      <span className="topbar__spacer" />
      <ConnIndicator health={health} />
      <RoleSelect role={role} onChange={onRoleChange} />
    </header>
  );
}

/* ---------- Plane nav ---------- */

export function PlaneNav({
  active,
  onNavigate,
}: {
  active: PlaneId;
  onNavigate: (id: PlaneId) => void;
}) {
  return (
    <nav className="nav" aria-label="Mesh planes">
      {PLANES.map((p) => {
        const isActive = p.id === active;
        return (
          <button
            key={p.id}
            type="button"
            className={`nav__item${isActive ? " nav__item--active" : ""}`}
            aria-current={isActive ? "page" : undefined}
            onClick={() => onNavigate(p.id)}
          >
            <span className="nav__rail" aria-hidden="true" />
            <span style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span className="nav__label">{p.label}</span>
              <span className="nav__plane">{p.plane}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}
